import { useState, useEffect, useRef } from 'react';
import Icon from '../../Icon/Icon';
import bem from '../../../../utils/bem';
import { useAnalyticsPushEvent } from '../../../../analytic/analytic.context';
import { viewSearchResults } from '../../../../utils/analitic.utils';

const Autocomplete = ({
  settings,
  children,
  setState,
  searchCallback = () => {},
  onOptionSelectAnalitycs,
  searchIcon = 'search-thick',
  onSearchAnalytics,
}) => {
  const pushAnalyticsEvent = useAnalyticsPushEvent();
  const baseClass = 'form-item';
  const listRef = useRef(null);
  const autocompleteRef = useRef(null);
  const {
    name,
    label,
    placeholder,
    disabled = false,
    state,
    clearButton = false,
    hidden = false,
    noSearchButton = false,
    loading = false,
    submitOnResultSelect = false,
  } = settings || {};

  const [clearing, setClearing] = useState(false);
  const [autocompleteEnabled, setAutocompleteEnabled] = useState(false);
  const [autocompleteSelectedValueId, setAutocompleteSelectedValueId] =
    useState('');
  const [focused, setFocused] = useState(false);
  const [clearButtonFlag, setClearButtonFlag] = useState(false);

  const analyticsOnSearch = () => {
    if (onSearchAnalytics) {
      if (onSearchAnalytics.event === 'list_filtered') {
        pushAnalyticsEvent({
          ...onSearchAnalytics,
          filter_value: autocompleteRef.current.value,
          filter_name: onSearchAnalytics.filter_name || label,
        });
      }
    }
  };

  const inputSubmit = (e) => {
    if (e) {
      e.preventDefault();
    }
    setAutocompleteEnabled(false);
    searchCallback(true, viewSearchResults({}));
    analyticsOnSearch();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        listRef?.current &&
        !listRef.current.contains(event.target) &&
        event.target !== autocompleteRef.current
      ) {
        setAutocompleteEnabled(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (state[`${name}`] === '' && clearing) {
      setClearing(false);
    }

    if (clearButtonFlag) {
      setClearButtonFlag(false);
      inputSubmit();
    }
  }, [state]);

  useEffect(() => {
    if (state.query?.length && submitOnResultSelect) {
      const viewSearchResultsAnalytics =
        autocompleteSelectedValueId !== ''
          ? viewSearchResults({ elementLocation: 'search-auto-complete' })
          : undefined;

      searchCallback(true, viewSearchResultsAnalytics);
      analyticsOnSearch();
    }
  }, [autocompleteSelectedValueId]);

  if (hidden) {
    return '';
  }

  const onChange = (event) => {
    const { value } = event.target;

    setState((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });

    setAutocompleteSelectedValueId('');
  };

  const clearButtonHandle = () => {
    setClearing(true);

    setState((prev) => {
      return {
        ...prev,
        [name]: '',
      };
    });
    setAutocompleteSelectedValueId('');

    autocompleteRef.current.focus();

    setClearButtonFlag(true);
  };

  const renderLabel = () => {
    if (!label) {
      return '';
    }

    return <label htmlFor={`field-${name}`}>{label}</label>;
  };

  const selectAutocompleteValue = (value, selectedValueId) => {
    setState((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
    setAutocompleteEnabled(false);

    if (selectedValueId) {
      setAutocompleteSelectedValueId(selectedValueId);
    }

    if (typeof onOptionSelectAnalitycs === 'function') {
      onOptionSelectAnalitycs(settings, {
        target: {
          value,
        },
      });
    }
  };

  return (
    <div
      className={bem(
        baseClass,
        ['autocomplete', `${clearButton ? 'clear-button' : ''}`],
        '',
      )}
    >
      {renderLabel()}
      <div
        className={bem('text-field-area', [], baseClass, [
          'relative',
          loading && focused ? 'is-loading' : '',
        ])}
      >
        <input
          ref={autocompleteRef}
          className="form-text form-item__textfield"
          id={`field-${name}`}
          type="text"
          name={name}
          placeholder={placeholder}
          disabled={disabled}
          value={state[`${name}`] || ''}
          onChange={onChange}
          onFocus={() => {
            setFocused(true);
            setAutocompleteEnabled(true);
          }}
          onKeyDown={(event) => {
            if (event.keyCode === window.ct.keyCodes.ent) {
              inputSubmit(event);
            }
          }}
          onBlur={() => setFocused(false)}
          data-selected-value-id={autocompleteSelectedValueId}
        />
        {children?.length && autocompleteEnabled ? (
          <ul
            className={bem('autocomplete-list', [], baseClass)}
            role="list"
            ref={listRef}
          >
            {children.map((child, index) => {
              const {
                id,
                value,
                searchValue,
                heading,
                modClass = '',
                isLink = false,
              } = child;

              // If the child is a heading, render it as such.
              if (heading) {
                return (
                  <li
                    key={`${heading}-${index}`}
                    className={bem(
                      'autocomplete-heading',
                      ['heading', modClass],
                      baseClass,
                    )}
                  >
                    {value}
                  </li>
                );
              }

              // if the child is a link, render it as such and do not update autocomplete field.
              if (isLink) {
                return (
                  <li
                    key={id}
                    className={bem(
                      'autocomplete-component',
                      [modClass],
                      baseClass,
                    )}
                  >
                    {value}
                  </li>
                );
              }

              // Return autocomplete list suggestion which will update input on click.
              return (
                <li
                  key={index}
                  tabIndex={0}
                  className={bem(
                    'autocomplete-list-item',
                    [modClass],
                    baseClass,
                  )}
                  onClick={() => selectAutocompleteValue(searchValue, id)}
                  onKeyUp={(event) => {
                    if (event.keyCode === window.ct.keyCodes.ent) {
                      selectAutocompleteValue(searchValue, id);
                    }
                  }}
                >
                  {value}
                </li>
              );
            })}
          </ul>
        ) : null}
        {clearButton && state[`${name}`] && (
          <button className="btn-icon btn-clear" onClick={clearButtonHandle}>
            <span className="visually-hidden">{Drupal.t('Clear value')}</span>
            <Icon iconName="close-small" />
          </button>
        )}
        <button
          className="btn-icon btn-search"
          onClick={inputSubmit}
          disabled={noSearchButton}
        >
          <span className="visually-hidden">{Drupal.t('Search')}</span>
          <Icon iconName={searchIcon} />
        </button>
      </div>
    </div>
  );
};

export default Autocomplete;
